import {
	AccessExplainerDetails,
	AccessExplainerObjectDetails,
	AccessObject,
	AccessType
} from '@sonar-software/sonar-core';
import { useCallback, useEffect, useMemo, useState } from 'react';

type AccessObjectWithId = AccessObject & { id: string };

interface UseAddPermissionObjectConfig {
	data?: AccessExplainerDetails | AccessExplainerObjectDetails | null;
	onAdditionalColumnsChange?: (columns: Array<AccessObjectWithId>) => void;
	onForceRefresh: () => void;
	isLoading?: boolean;
}

interface UseAddPermissionObjectReturn {
	additionalColumnIds: Array<string>;
	combinedAccessObjects: Record<string, AccessObject>;
	addPermissionColumn: (label: string, type: AccessType, value: string) => void;
	removePermissionColumn: (id: string) => boolean;
	handleForceRefresh: () => void;
}

export function useAddPermissionObject({
	data,
	onAdditionalColumnsChange,
	onForceRefresh,
	isLoading
}: UseAddPermissionObjectConfig): UseAddPermissionObjectReturn {
	const [ additionalColumns, setAdditionalColumns ] = useState<Array<AccessObjectWithId>>([]);
	const [ isRefreshing, setIsRefreshing ] = useState(false);

	const addPermissionColumn = useCallback(
		(label: string, type: AccessType, value: string) => {
			const newAccessObject: AccessObjectWithId = {
				id: value,
				name: label,
				sourceUrl: undefined,
				type
			};

			setAdditionalColumns((prev) => {
				if (prev.some((c) => c.id === newAccessObject.id)) {
					return prev;
				}
				const updated = [ ...prev, newAccessObject ];

				return updated;
			});
		},
		[ ]
	);

	useEffect(() => {
		onAdditionalColumnsChange?.(additionalColumns);
	}, [ additionalColumns, onAdditionalColumnsChange ]);

	const removePermissionColumn = useCallback((id: string) => {
		let removed = false;

		setAdditionalColumns((prev) => {
			const exists = prev.some((col) => col.id === id);
			if (!exists) {
				return prev;
			}
			removed = true;

			return prev.filter((col) => col.id !== id);
		});

		return removed;
	}, []);

	const handleForceRefresh = useCallback(() => {
		setIsRefreshing(true);
		onForceRefresh();
	}, [ onForceRefresh ]);

	// Clear additional columns only after refresh completes
	useEffect(() => {
		if (isRefreshing && !isLoading) {
			setAdditionalColumns([]);
			setIsRefreshing(false);
		}
	}, [ isRefreshing, isLoading ]);

	// Build the final collection, adding only brand new columns
	const combinedAccessObjects = useMemo(() => {
		const base = data?.accesses || {};
		const result = { ...base };

		additionalColumns.forEach((col) => {
			const key = col.id;
			if (!Object.values(base).some((existing) =>
				existing.name === col.name && existing.type === col.type)) {
				result[ key ] = col;
			}
		});

		return result;
	}, [ data, additionalColumns ]);

	const additionalColumnIds = useMemo(
		() => additionalColumns.map((col) => col.id || `${col.name}_${col.type}`),
		[ additionalColumns ]
	);

	return {
		addPermissionColumn,
		additionalColumnIds,
		combinedAccessObjects,
		handleForceRefresh,
		removePermissionColumn
	};
}
